import { useEffect, useState } from "react";
import { api, trackView, setPageMeta } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import PostListRow from "@/components/PostListRow";
import PostDialog from "@/components/PostDialog";
import { Search } from "lucide-react";

const Archive = () => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 25;
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    setPageMeta({ title: "Archivio", url: window.location.href });
    trackView("/archivio");
  }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/posts", { params: { q, page, per_page: perPage } });
        if (cancel) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      } finally {
        if (!cancel) setLoading(false);
      }
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, page]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div data-testid="archive-page" className="min-h-screen bg-[#f9f8f6]">
      <PublicHeader />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-24">
        <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#4a4843] mb-3">Archivio</p>
        <h1 className="font-serif text-4xl sm:text-6xl tracking-tight leading-none font-light text-[#1a1918] mb-8 sm:mb-10">
          Tutti gli attraversamenti
        </h1>

        <div className="relative mb-10 sm:mb-12 max-w-xl">
          <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4a4843]" />
          <input
            data-testid="archive-search"
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="cerca un titolo…"
            className="w-full bg-transparent border-0 border-b border-[#1a1918] py-3 pl-7 pr-2 font-serif text-lg focus:outline-none focus:border-[#d4b886]"
          />
        </div>

        {loading ? (
          <p data-testid="archive-loading" className="font-sans text-sm text-[#4a4843]">caricamento…</p>
        ) : items.length === 0 ? (
          <p data-testid="archive-empty" className="font-serif italic text-xl text-[#4a4843]">
            Nessun risultato.
          </p>
        ) : (
          <ul className="divide-y divide-[#e5e1d8] border-t border-b border-[#e5e1d8]" data-testid="archive-list">
            {items.map((p) => (
              <li key={p.id} data-testid={`archive-item-${p.id}`}>
                <PostListRow post={p} onOpen={setOpenId} variant="compact" />
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-between gap-3">
            <button
              data-testid="archive-prev"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="font-sans text-xs tracking-[0.3em] uppercase border-b border-[#1a1918] pb-1 disabled:opacity-30 py-2"
            >
              ← prec
            </button>
            <span className="font-sans text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#4a4843]">
              {page} / {totalPages}<span className="hidden sm:inline"> — {total} post</span>
            </span>
            <button
              data-testid="archive-next"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="font-sans text-xs tracking-[0.3em] uppercase border-b border-[#1a1918] pb-1 disabled:opacity-30 py-2"
            >
              succ →
            </button>
          </div>
        )}
      </section>

      <PostDialog postId={openId} open={!!openId} onOpenChange={(v) => !v && setOpenId(null)} />
      <PublicFooter />
    </div>
  );
};

export default Archive;
